import { useState, useEffect, useCallback, useRef } from 'react';
import locationService, {
  type LocationResult,
  type LocationError,
  type WatchId,
} from '../services/location';
import { requestLocationWithDisclosure } from '../services/locationGate';

export type LocationPermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported' | 'unknown';

export interface UseLocationState {
  /** Most recent location reading, or null if not yet available. */
  location: LocationResult | null;
  /** Most recent location error, or null when no error. */
  error: LocationError | null;
  /** True while a `requestLocation()` fetch is in-flight. */
  loading: boolean;
  /** Current permission state (if the Permissions API is available). */
  permission: LocationPermissionState;
  /**
   * Trigger a one-shot location fetch.  Clears any previous error.
   * Returns the result (or throws a LocationError on failure).
   */
  requestLocation: () => Promise<LocationResult | null>;
}

export interface UseLocationOptions {
  /** If true, start watching the position immediately on mount. */
  watch?: boolean;
  /**
   * Passed directly to the Geolocation API.
   * When using the `watch` option, memoize this object (e.g. with useMemo)
   * so that the watch is not restarted on every render.
   */
  positionOptions?: PositionOptions;
}

/**
 * React hook that wraps the LocationService.
 *
 * Usage (one-shot fetch):
 *   const { location, error, loading, requestLocation } = useLocation();
 *
 * Usage (continuous watch):
 *   const opts = useMemo(() => ({ enableHighAccuracy: true }), []);
 *   const { location, error } = useLocation({ watch: true, positionOptions: opts });
 */
export function useLocation(opts: UseLocationOptions = {}): UseLocationState {
  const { watch = false, positionOptions } = opts;

  const [location, setLocation] = useState<LocationResult | null>(null);
  const [error, setError] = useState<LocationError | null>(null);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<LocationPermissionState>('unknown');
  const watchIdRef = useRef<WatchId>(-1);
  // Store positionOptions in a ref so the watch effect is not restarted when
  // the caller passes a new object reference with the same values.
  const positionOptionsRef = useRef(positionOptions);
  positionOptionsRef.current = positionOptions;

  // Check and track the Permissions API state where supported.
  useEffect(() => {
    if (!locationService.isSupported()) {
      setPermission('unsupported');
      return;
    }

    if (!navigator.permissions) return;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then(status => {
        setPermission(status.state as LocationPermissionState);
        status.onchange = () => setPermission(status.state as LocationPermissionState);
      })
      .catch(() => {
        // Permissions API not available — remain 'unknown'
      });
  }, []);

  // Start/stop watching when the `watch` option is active.
  // positionOptions is read from a ref so this effect only re-runs when
  // the `watch` flag itself changes, preventing unnecessary restarts.
  useEffect(() => {
    if (!watch) return;

    watchIdRef.current = locationService.watchPosition(
      result => {
        setLocation(result);
        setError(null);
      },
      err => setError(err),
      positionOptionsRef.current,
    );

    return () => {
      locationService.clearWatch(watchIdRef.current);
      watchIdRef.current = -1;
    };
  }, [watch]);

  const requestLocation = useCallback(async (): Promise<LocationResult | null> => {
    if (!locationService.isSupported()) {
      const err: LocationError = {
        code: 'UNSUPPORTED',
        message: 'Geolocation is not supported in this environment.',
      };
      setError(err);
      return null;
    }

    setLoading(true);
    setError(null);

    // Route through the disclosure gate so first-time users see the in-app
    // consent modal before the OS permission prompt. The gate never throws;
    // it returns null when the OS permission is denied or the fix fails.
    const result = await requestLocationWithDisclosure(positionOptionsRef.current);
    setLoading(false);
    if (result) {
      setLocation(result);
      return result;
    }
    setError({ code: 'PERMISSION_DENIED', message: 'Location unavailable.' });
    return null;
  }, []);

  return { location, error, loading, permission, requestLocation };
}

export default useLocation;
